<template :class="classes">
  <div class="default-card" :class="classes" :data-category="`${eyebrow}`" @click="handleCardClick">
    <!-- VIEW-ALL VARIANT: the trailing card of a row. It reuses the card shell,
         so the row sizes it like every other card, and MyButton for the CTA
         styling — no bespoke card component. -->
    <MyButton
      v-if="viewAll"
      class="view-all-cta"
      type="outline"
      size="large"
      :label="label || 'View All'"
      :route="activeRoute"
      :link="activeLink"
    />

    <template v-else>
      <div v-if="alt" class="image" :style="bgcolor">
        <!-- Show placeholder when no image -->
        <template v-if="!hasImage">
          <router-link v-if="activeRoute && !activeLink" :to="activeRoute">
            <div
              class="placeholder"
              :style="{ backgroundColor: placeholderColor }"
              v-html="placeholderSvg"
            ></div>
          </router-link>
          <a v-else-if="activeLink" :href="activeLink" target="_blank" rel="noopener noreferrer">
            <div
              class="placeholder"
              :style="{ backgroundColor: placeholderColor }"
              v-html="placeholderSvg"
            ></div>
          </a>
          <div
            v-else
            class="placeholder"
            :style="{ backgroundColor: placeholderColor }"
            v-html="placeholderSvg"
          ></div>
        </template>

        <!-- Show images when available -->
        <template v-else>
          <router-link v-if="activeRoute && !activeLink" :to="activeRoute">
            <img
              v-if="imgurl"
              :src="imgurl"
              :alt="`${alt}`"
              :class="`image-${imageVariant}`"
              loading="lazy"
            />
            <img
              draggable="false"
              v-if="filename"
              :class="`image-${imageVariant}`"
              :src="require(`../../../assets/images/${filename}`)"
              :alt="`${alt}`"
              loading="lazy"
            />
          </router-link>
          <a v-else-if="activeLink" :href="activeLink" target="_blank" rel="noopener noreferrer">
            <img
              v-if="imgurl"
              :src="imgurl"
              :alt="`${alt}`"
              :class="`image-${imageVariant}`"
              loading="lazy"
            />
            <img
              draggable="false"
              v-if="filename"
              :class="`image-${imageVariant}`"
              :src="require(`../../../assets/images/${filename}`)"
              :alt="`${alt}`"
              loading="lazy"
            />
          </a>
          <div v-else>
            <img
              v-if="imgurl"
              :src="imgurl"
              :alt="`${alt}`"
              :class="`image-${imageVariant}`"
              loading="lazy"
            />
            <img
              draggable="false"
              v-if="filename"
              :class="`image-${imageVariant}`"
              :src="require(`../../../assets/images/${filename}`)"
              :alt="`${alt}`"
              loading="lazy"
            />
          </div>
        </template>
        <div v-if="isEffectivelyLocked" class="lock-overlay">
          <span class="lock-label">Client Work</span>
          <span class="lock-message">Request Access</span>
        </div>
      </div>

      <div class="info">
        <!-- Color bar at top when no image (COMMENTED OUT - using placeholder images instead) -->
        <!-- <div v-if="!hasImage" class="color-bar" :style="{ backgroundColor: typeColorSubtle }"></div> -->

        <TextBlock
          clamped
          class="textblock"
          :class="{ 'textblock--mobile-list': mobileList }"
          :eyebrow="displayEyebrow"
          as="h4"
          :icon="icon"
          :iconsize="iconsize"
          :title="title"
          :titleRoute="activeRoute || activeLink"
          :description="description"
          :tags="tags"
          :cardType="type"
          :readTime="readTime"
          :date="date"
          :complete="read"
          :isExternal="!!activeLink"
          @tag-click="$emit('tag-click', $event)"
        />

        <!-- :description="mobileList ? title : description" -->

        <!-- Read more link -->
        <!-- <div class="card-footer">
          <TextLink :label="label || 'Read more'" :route="route || btnroute" :link="link" />
        </div> -->
      </div>
    </template>
  </div>
</template>

<script>
import TextBlock from '../../text/TextBlock/TextBlock.vue';
import MyButton from '../../Button/Button.vue';
import { getReadTime } from '../../../utils/readTime';
import { generateMotif } from '../../../utils/motifPlaceholder';

export default {
  name: 'ArticleCard',
  components: {
    TextBlock,
    MyButton,
    //TextLink,
  },
  props: {
    imgurl: {
      type: String,
    },
    filename: {
      type: String,
      required: false,
    },
    filename1: {
      type: String,
      required: false,
    },
    filename2: {
      type: String,
      required: false,
    },
    filename3: {
      type: String,
      required: false,
    },
    alt: {
      type: String,
    },
    eyebrow: {
      default: '',
      required: false,
      type: String,
    },
    icon: {
      type: String,
      required: false,
    },
    iconsize: {
      type: String,
      required: false,
    },
    title: {
      default: 'Hello World',
      required: true,
      type: String,
    },
    description: {
      default: '',
      required: false,
      type: String,
    },

    route: {
      default: '',
      type: String,
      required: false,
    },
    btnroute: {
      default: '',
      type: String,
      required: false,
    },
    link: {
      default: '',
      type: String,
      required: false,
    },
    label: {
      type: String,
    },
    cover: {
      type: Boolean,
      default: false,
    },
    // Renders the card as the row's trailing "View All" call to action: the
    // card shell stays, its contents become a single button.
    viewAll: {
      type: Boolean,
      default: false,
    },
    borderless: {
      type: Boolean,
      default: false,
    },
    list: {
      type: Boolean,
      default: false,
    },
    mobileList: {
      type: Boolean,
      default: false,
    },
    indexRow: {
      type: Boolean,
      default: false,
    },
    featured: {
      type: Boolean,
      default: false,
    },
    tags: {
      type: Array,
      default: () => [],
      required: false,
    },
    index: {
      type: Number,
      default: 0,
      required: false,
    },
    type: {
      type: String,
      default: 'article',
      required: false,
    },
    bgcolor: {
      type: String,
      required: false,
    },
    imageVariant: {
      type: String,
      default: 'full',
      required: false,
      validator: (value) => ['full', 'offset', 'angled', 'float'].includes(value),
    },
    contentFile: {
      type: String,
      default: '',
      required: false,
    },
    date: {
      type: String,
      default: '',
      required: false,
    },
    locked: {
      type: Boolean,
      default: false,
      required: false,
    },
    read: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    classes() {
      return {
        defaultcard: true,
        'defaultcard--cover': this.cover,
        'defaultcard--view-all': this.viewAll,
        'defaultcard--borderless': this.borderless,
        'defaultcard--list': this.list,
        'defaultcard--mobile-list': this.mobileList,
        'defaultcard--index-row': this.indexRow,
        'defaultcard--featured': this.featured,
        'defaultcard--locked': this.isEffectivelyLocked,
        // A project card: a screenshot on its own colour (work, case studies).
        'defaultcard--project': !!this.bgcolor && this.hasImage,
      };
    },
    isDev() {
      return process.env.NODE_ENV === 'development';
    },
    isEffectivelyLocked() {
      // Locked in production, unlocked in development
      return this.locked && !this.isDev;
    },
    activeRoute() {
      return this.isEffectivelyLocked ? '' : this.route || this.btnroute;
    },
    activeLink() {
      return this.isEffectivelyLocked ? '' : this.link;
    },
    displayEyebrow() {
      return this.eyebrow;
    },
    hasImage() {
      // The old static placeholders (images/placeholders/placeholder-NN.svg)
      // count as no image, so those cards get their motif drawing instead.
      const src = this.imgurl || this.filename || this.filename1 || this.filename3;
      return !!src && !/placeholder-\d+/.test(String(src));
    },
    typeColor() {
      // Color mapping based on article type (darker variants for text)
      const typeColorMap = {
        article: 'var(--color-yellow)',
        tool: 'var(--color-yellow)',
        'case-study': 'var(--color-yellow)',
        'design-project': 'var(--color-yellow)',
      };

      return typeColorMap[this.type] || '#0066b3';
    },
    typeColorSubtle() {
      // Create subtle/transparent variants for backgrounds
      const subtleColorMap = {
        article: 'var(--color-lightyellow)',
        tool: 'var(--color-lightyellow)',
        'case-study': 'var(--color-lightyellow)',
        'design-project': 'var(--color-lightyellow)',
      };

      return subtleColorMap[this.type] || 'rgba(0, 134, 230, 0.15)';
    },
    placeholderColor() {
      // Neutral, theme-aware base so the generated brand gradient reads true in
      // both light and dark. The gradient wash fades to transparent at the top,
      // letting this base show through and blend with the card.
      return 'var(--background)';
    },
    placeholderSvg() {
      // Deterministic per card: same title+destination always yields the same
      // drawing, so the placeholder never changes between renders. These
      // replaced the generated gradient mesh (gradientPlaceholder.ts).
      const seed = `${this.title}|${this.route || this.btnroute || this.link || ''}`;
      return generateMotif(seed, this.title);
    },
    readTime() {
      return getReadTime(this.contentFile);
    },
  },
  methods: {
    handleCardClick(e) {
      if (this.isEffectivelyLocked) {
        e.preventDefault();
        e.stopPropagation();
        this.$emit('request-access', this.title);
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* Project cards on the home page are inverse: brown with banana text in the
   light theme, banana with brown text in the dark. The screenshot is drawn
   in the same two colours by the duotone filters in HomePage.vue, the
   opposite way round to the tile: in the light theme a dark screenshot reads
   yellow with brown text (#site-duotone-dark), in the dark it reads brown
   with banana text (#site-duotone), on hover as well. Everywhere else (the
   Library, related work on articles) the cards keep their project colour.
   App.vue puts .is-home on <html> on the home route. */
:root.is-home .defaultcard--project {
  --tile-bg: var(--color-skin-deep);
  --tile-ink: var(--color-banana);
  --tile-ink-rgb: var(--color-banana-rgb);
  --tile-filter: contrast(1.2) url(#site-duotone-dark);
  // !important to beat the card's inline project colour.
  .image {
    background-color: var(--tile-bg) !important;
  }
  // The duotone holds on hover too; only the card's zoom moves.
  .image img {
    filter: var(--tile-filter);
  }
}
:root.is-home.dark-theme .defaultcard--project {
  --tile-bg: var(--color-banana);
  --tile-ink: var(--color-skin-deep);
  --tile-ink-rgb: 56, 33, 28;
  --tile-filter: contrast(1.2) url(#site-duotone);
}
@media (prefers-color-scheme: dark) {
  :root.is-home:not(.light-theme) .defaultcard--project {
    --tile-bg: var(--color-banana);
    --tile-ink: var(--color-skin-deep);
    --tile-ink-rgb: 56, 33, 28;
    --tile-filter: contrast(1.2) url(#site-duotone);
  }
}
@mixin list-row-base {
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none;
  min-height: auto;
  // The subtle rule, not the full ink line: a divider between rows.
  border-block-start: var(--rule-inner) !important;
  padding-block: var(--spacing-xs);
  // The card clips for its hover zoom; a row has no padding left to absorb
  // that, so clipping would cut the meta line's descenders. The row's image
  // frame clips itself.
  overflow: visible !important;

  &:hover {
    background: transparent;
    box-shadow: none !important;
  }

  .image {
    display: none !important;
  }

  .info {
    padding: 0 !important;
  }
}

* {
  border-radius: 0;
}

.default-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: var(--spacing-xxs) !important;
  border: var(--border);
  // background: var(--color-lightyellow);
  overflow: hidden;
  -moz-transition: all 0.25s ease-in-out;
  -o-transition: all 0.25s ease-in-out;
  -webkit-transition: all 0.25s ease-in-out;
  transition: all 0.25s ease-in-out;
  box-shadow: var(--shadow-z2);
  min-height: 400px;
  height: 100%;

  @media only screen and (max-width: 767px) {
    grid-column: 1 / 4;
    width: 100%;
    height: auto;
    min-height: 300px;
  }

  &:hover {
    background: var(--background);
    box-shadow: var(--shadow-z5);
    // transform: scale(1.01);

    img:not(.image-offset):not(.image-angled):not(.image-float) {
      transform: scale(1.1);
    }

    .image-float {
      transform: scale(0.86);
    }

    .image-offset {
      transform: scale(1.05) translateX(15%);
    }

    .image-angled {
      transform: scale(1.02) rotate(-7deg) translateX(18%) translateY(var(--spacing-md));
    }
  }
  // &:active {
  //   box-shadow: var(--shadow-hover);
  //   transform: rotate(1deg);
  // }
}
.info {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  padding: 0 var(--spacing-md) var(--spacing-md);
  justify-content: space-between;

  &:has(.color-bar) {
    padding-block-start: 0;
  }

  &:not(:has(.color-bar)) {
    padding-block-start: var(--spacing-sm);
  }
}

.textblock {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-footer {
  margin-block-start: auto;
  margin-block-end: var(--spacing-xxs);

  padding-block-start: var(--spacing-sm);
}

/* Locked card state */
.defaultcard--locked {
  cursor: help;

  &:hover .lock-overlay {
    opacity: 1;
  }
}

.lock-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xxs);
  opacity: 0;
  transition: opacity 0.25s ease-in-out;
  z-index: 10;
  pointer-events: none;
}

.lock-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: var(--font-300);
  --reading-fontWeight: var(--font-300-weight);
  font-weight: var(--font-300-weight);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.lock-message {
  color: white;
  font-size: var(--font-500);
  --reading-fontWeight: var(--font-500-weight);
  font-weight: var(--fontWeight-medium);
}

img {
  // Card images carry no ink line (images.scss gives raster images one).
  outline: none !important;
  inline-size: 100%;
  block-size: 101% !important;
  object-fit: cover !important;
  max-width: 100%;
  border-radius: 0 !important;
  -moz-transition: transform 0.25s ease-in-out;
  -o-transition: transform 0.25s ease-in-out;
  -webkit-transition: transform 0.25s ease-in-out;
  transition: transform 0.25s ease-in-out;
}

.filename2 {
  z-index: 1;
  inset-block-start: 14.75%;
  inset-inline-end: -18%;
  block-size: 100%;
  border-radius: 0.6rem 0 0 0.6rem !important;
  object-fit: cover !important;
  object-position: 0% 0% !important;
}

.filename3 {
  z-index: 1;
  inset-inline-start: 20%;
  inset-block-start: 15%;
  block-size: 100%;
  rotate: -7deg;
  border-radius: 0.6rem 0 0 0 !important;
  object-fit: cover !important;
  object-position: 0% 0% !important;
}

// A single screenshot sits flat on its tile. Where a card stacks more than one
// image, each is lifted off the one below by a soft shadow (--shadow-screen,
// stronger in the dark theme, where a light one doesn't show).
.image:has(img + img, img ~ * img) {
  .image-offset,
  .image-angled {
    box-shadow: var(--shadow-screen);
  }
}
.image-offset {
  transform: scale(0.95) translateX(15%);
  transform-origin: top right;
  border-radius: 0.6rem !important;
  object-position: 0 0 !important;
}

// Screens in a card sit on the project's colour field with the 0.6rem screen
// corner and no outline: the field already separates them from the page.
.image-angled {
  transform: scale(0.95) rotate(-7deg) translateX(18%) translateY(var(--spacing-md));
  transform-origin: top right;
  border-radius: 0.6rem !important;
  object-position: 0 0 !important;
}

// The whole screen, framed in the middle of the tile with an even margin.
.image-float {
  transform: scale(0.8);
  transform-origin: center;
  border-radius: 0.6rem !important;
  object-position: 0 0 !important;
}

.image {
  overflow: hidden;
  border-radius: 0 !important;
  position: relative;
  border-radius: var(--spacing-xxxs) !important;
  margin: var(--spacing-xs);

  &:has(img),
  &:has(.placeholder) {
    aspect-ratio: 5/4;
  }

  // No frame line: a card's image is a wash shape (the figure stage, or the
  // project's colour field), and the fill is its edge. Ink lines are kept for
  // things that are drawn (the figures inside) and things you press.
}

.image a,
.image router-link {
  text-decoration: none;
  display: block;
  height: 100%;
}

.placeholder {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  // The motif drawing (motifPlaceholder.ts): the figure stage behind it, its
  // lines in the page's ink, and cqi sizing for the type motifs.
  background-image: linear-gradient(var(--background-darker), var(--background-darker));
  color: var(--foreground);
  container-type: inline-size;
}

.placeholder svg {
  display: block;
  width: 100%;
  height: 100%;
}

.color-bar {
  width: calc(100% + var(--spacing-md) * 2);
  height: var(--spacing-xxs);
  margin-inline-start: calc(var(--spacing-md) * -1);
  margin-block-start: 0;
  margin-block-end: var(--spacing-sm);
  -moz-transition: height 0.25s ease-in-out;
  -o-transition: height 0.25s ease-in-out;
  -webkit-transition: height 0.25s ease-in-out;
  transition: height 0.25s ease-in-out;
}

// Canonical list-row style (single source of truth). Used by the Library list
// view and the course hub. Reproduces the Library list card exactly: a
// top-bordered row, no image, full-size title + description.
.defaultcard--list {
  @media only screen and (min-width: 768px) {
    @include list-row-base;
    height: auto !important;
    padding-block-start: var(--spacing-sm);
    display: grid !important;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: var(--spacing-xs);

    &:first-child {
      border-block-start: none !important;
    }

    // A list row is often also `borderless`, whose `border: none` would drop
    // the divider. The compound selector outranks it so the top-border divider
    // survives.
    &.defaultcard--borderless {
      border-block-start: var(--rule-inner) !important;

      &:first-child {
        border-block-start: none !important;
      }
    }

    .image {
      grid-column: 3 / 4;
      grid-row: 1;
      margin: 0 !important;
      aspect-ratio: 1 / 1 !important;
      inline-size: 100% !important;

      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
        aspect-ratio: 1 / 1;
        display: none;
      }

      .placeholder {
        aspect-ratio: 1 / 1;
        block-size: 100%;
        inline-size: 100%;
      }
    }

    .info {
      // Spans two of three columns, leaving the third empty — this is where a
      // conditional field (e.g. the completion check) sits without reflowing.
      grid-column: 1 / 3;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;

      &:not(:has(.color-bar)) {
        padding-block-start: 0 !important;
      }
    }

    // A list row is often also `borderless`. The borderless variant adds its
    // own info padding; the list layout must win so rows stay flush. The
    // compound selector outranks `.defaultcard--borderless .info` regardless of
    // source order.
    &.defaultcard--borderless .info {
      padding: 0 !important;

      &:not(:has(.color-bar)) {
        padding-block-start: 0 !important;
      }
    }

    .card-footer {
      padding-block-start: var(--spacing-xxs);
      margin-block-start: auto;
    }

    .color-bar {
      width: 100%;
      margin-inline-start: 0;
    }
  }
}

.defaultcard--borderless {
  border: none !important ;
  box-shadow: none !important;

  &:hover {
    background: transparent;
    box-shadow: none;
  }
  // A cover card keeps its own inset (.defaultcard--cover .info): its text
  // sits over the image, so it can't run to the card's edge like these do.
  &:not(.defaultcard--cover) .info {
    padding: 0 !important;
    margin-block-start: 0;

    &:not(:has(.color-bar)) {
      padding-block-start: var(--spacing-sm) !important;
    }
  }
  @media only screen and (min-width: 1201px) {
    &:not(.defaultcard--cover) .info {
      padding: 0 0 var(--spacing-md) 0 !important;
      margin-block-start: var(--spacing-xxs);

      &:not(:has(.color-bar)) {
        padding-block-start: var(--spacing-xs) !important;
      }
    }
  }

  .image {
    border-radius: var(--spacing-xxs) !important;
    margin: 0 !important;
  }

  .color-bar {
    width: 100%;
    margin-inline-start: 0;
  }
}
.defaultcard--mobile-list {
  @media only screen and (max-width: 767px) {
    @include list-row-base;
    min-height: 0 !important;
    height: auto !important;
    // Symmetric padding around the top-border divider so the mobile list row
    // reads evenly above and below the line (paired with the removed flex gap
    // on the mobile list container).
    padding-block: var(--spacing-sm);
    display: grid !important;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: var(--spacing-xs);

    // The first row starts the list, so it has no divider above it (matches
    // the desktop list variant).
    &:first-child {
      border-block-start: none !important;
    }

    .image {
      grid-column: 3 / 4;
      grid-row: 1;
      margin: 0 !important;
      aspect-ratio: 1 / 1 !important;
      inline-size: 100% !important;

      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
        aspect-ratio: 1 / 1;
      }

      .placeholder {
        aspect-ratio: 1 / 1;
        block-size: 100%;
        inline-size: 100%;
      }
    }

    .info {
      grid-column: 1 / 4;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;

      &:not(:has(.color-bar)) {
        padding-block-start: 0 !important;
      }
    }

    .card-footer {
      padding-block-start: var(--spacing-xxs);
      margin-block-start: auto;
    }

    .color-bar {
      width: 100%;
      margin-inline-start: 0;
    }
  }
}

.defaultcard--index-row {
  @include list-row-base;
  display: flex !important;
  flex-direction: row;
  align-items: baseline;
  grid-column: 1 / -1;

  .info {
    flex: 1;
  }

  .textblock {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
  }

  .textblock :deep(.description) {
    display: none !important;
  }

  .textblock :deep(.title) {
    font-size: var(--font-400);
    --reading-fontWeight: var(--font-400-weight);
    font-weight: var(--font-400-weight);
    line-height: var(--lineHeight-base);
  }

  .textblock :deep(.title-link:hover .title) {
    text-decoration: underline;
    text-underline-offset: 0.2em;
    text-decoration-thickness: 0.15rem;
  }

  .textblock :deep(.eyebrow) {
    font-size: var(--font-2xs);
    --reading-fontWeight: var(--font-2xs-weight);
    font-weight: var(--font-2xs-weight);
    color: var(--foreground-muted);
  }

  .textblock :deep(.tags--content) {
    margin-block-start: 0;
    padding-block-start: 0;
    flex-shrink: 0;
    align-self: baseline;
    justify-content: flex-end;
  }
}

/* VIEW-ALL VARIANT
   The row's trailing card. The shell keeps the card's box — the row stretches
   it to the tallest card's height, and the scroller gives it the same width —
   but drops its own border, fill and shadow so the only thing drawn is the
   button, which covers the card edge to edge. */
.defaultcard--view-all {
  border: none;
  box-shadow: none;
  background: transparent;

  &:hover {
    background: transparent;
    box-shadow: none;
  }
}

.view-all-cta {
  display: flex;
  flex: 1;
  block-size: 100%;
  inline-size: 100%;

  // The card's link wrapper picks up the global underline for anchors; the
  // button inside already carries the affordance, so drop it here.
  :deep(a) {
    display: flex;
    inline-size: 100%;
    block-size: 100%;
    text-decoration: none;
  }

  :deep(.custom-btn) {
    display: flex;
    align-items: center;
    justify-content: center;
    inline-size: 100%;
    block-size: 100%;
  }
}

.defaultcard--cover {
  background-color: transparent;
  &:hover {
    background: transparent;
  }
  aspect-ratio: 3/4;

  // @media only screen and (min-width: 768px) {
  //   aspect-ratio: auto;
  // }
  .info {
    display: flex !important;
    flex-direction: column !important;
    flex: 1;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: var(--spacing-sm) var(--spacing-md) calc(var(--spacing-md) + var(--spacing-xs));
    z-index: 100;
    //    background: linear-gradient(15deg, var(--background) 45%, rgba(0, 0, 0, 0) 170%);
    background: linear-gradient(135deg, var(--background) 0%, rgba(255, 255, 255, 0) 200%);
  }
  .textblock {
    background: transparent !important;
    height: 100%;
    display: flex !important;
    flex-direction: column !important;
  }
  .textblock .eyebrow {
    margin-block-start: auto;
  }
  .image {
    overflow: hidden;
    block-size: 100%;
    inline-size: 100%;
    border-radius: 0 !important;
    position: absolute;
    margin: 0 !important;
  }
}

.defaultcard--featured {
  @media only screen and (min-width: 768px) and (max-width: 1200px) {
    grid-column: span 2;
  }

  @media only screen and (min-width: 1201px) {
    grid-column: 1 / -1;
    min-height: auto;
    display: grid !important;
    // Three equal columns with the grid's own gap, so the image fills the
    // first two and the text starts exactly where the third column does.
    // Its own tracks rather than subgrid, so nothing inside the card can
    // resize the grid's columns.
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: inherit;

    // A cover keeps the card's own border and shadow, so borderless works
    // on it as it does on the default card.
    &:not(.defaultcard--cover) {
      border: none !important;
      box-shadow: none !important;

      &:hover {
        background: transparent;
        box-shadow: none;
      }
    }

    .image {
      grid-column: 1 / 3;
      grid-row: 1;
      margin: 0 !important;
      aspect-ratio: 16 / 9;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover !important;
        object-position: 50% 0;
      }
    }

    .info {
      grid-column: 3 / 4;
      grid-row: 1;
      min-width: 0; // its one-line meta row mustn't widen the shared column
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-self: stretch;
      padding: var(--spacing-md) 0 !important; // flush with the column's edge
      height: auto;
    }

    .textblock {
      flex: 0;
    }

    .textblock :deep(.title) {
      font-size: var(--font-700);
      --reading-fontWeight: var(--font-700-weight);
      font-weight: var(--font-700-weight);
    }
  }
}

/* Cover and featured together: one wide tile, the full row of the grid, with
   the screenshot filling it and the text laid over its empty left third (the
   angled screen is pushed right to leave room for it). Below 740px it's
   ImageCard's split instead: the text on top and the screenshot under it, in
   one panel on --background-darker, with no gradient over the image. */
@media only screen and (max-width: 739px) {
  .defaultcard--cover.defaultcard--featured {
    aspect-ratio: auto;
    gap: var(--spacing-md);
    background-color: var(--background-darker) !important;

    .info {
      position: static;
      order: -1;
      height: auto;
      background: none;
      padding: var(--spacing-md) var(--spacing-md) 0 !important;
    }

    .textblock {
      height: auto;
    }

    .textblock .eyebrow {
      margin-block-start: 0;
    }

    .image {
      position: relative;
      aspect-ratio: 4 / 3;
    }
  }
}
@media only screen and (min-width: 740px) {
  .defaultcard--cover.defaultcard--featured {
    grid-column: 1 / -1;
    display: block !important;
    position: relative;
    aspect-ratio: 16 / 9;
    min-height: 0;

    @media only screen and (min-width: 1201px) {
      aspect-ratio: 21 / 9;
    }

    &:hover {
      background: transparent;
    }

    // Square: the card's own radius clips it, so the tile and the card's
    // edge are one shape, the same radius as every other card.
    .image {
      position: absolute;
      inset: 0;
      aspect-ratio: auto;
      margin: 0 !important;
      border-radius: 0 !important;
    }

    .image-angled {
      transform: scale(0.9) rotate(-7deg) translateX(38%) translateY(var(--spacing-lg));
    }
    &:hover .image-angled {
      transform: scale(0.92) rotate(-7deg) translateX(36%) translateY(var(--spacing-lg));
    }

    .info {
      position: absolute;
      inset: 0 auto 0 0;
      inline-size: calc(100% / 3);
      min-width: 0;
      background: none;
      justify-content: flex-end;
      padding: var(--spacing-lg) !important;
    }

    .textblock {
      flex: 0;
    }
  }
}
/* The home work card is one layer at every size: the whole card takes the
   tile's colour, and its text is the tile's ink. */
:root.is-home .defaultcard--cover.defaultcard--featured.defaultcard--project {
  background-color: var(--tile-bg) !important;
  .info {
    color: var(--tile-ink);
    --foreground: var(--tile-ink);
    --foreground-rgb: var(--tile-ink-rgb);
    --foreground-muted: color-mix(in srgb, var(--tile-ink) 75%, transparent);
    --text-muted: var(--foreground-muted);
    --link: var(--tile-ink);
    :deep(p) {
      color: var(--foreground-muted) !important;
    }
  }
}
</style>
